import { isBrowserOnline } from '../../../hooks/useOnlineStatus';

export type ApiValidationParsed = {
  summary: string;
  fieldErrors: Record<string, string>;
};

/** Mensagens genéricas do Pydantic → texto mais claro para o usuário (pt-BR). */
export function friendlyValidationMessage(msg: string): string {
  const t = msg.trim();
  if (t === 'Input should be a valid string') {
    return 'Preenchimento obrigatório ou texto inválido.';
  }
  if (t === 'Input should be a valid integer') {
    return 'Informe um número inteiro válido.';
  }
  if (t === 'Input should be a valid number') {
    return 'Informe um número válido.';
  }
  const minLen = t.match(/^String should have at least (\d+) characters?$/i);
  if (minLen) {
    return `Informe pelo menos ${minLen[1]} caracteres.`;
  }
  return t;
}

function lastLocSegment(loc: unknown): string | null {
  if (!Array.isArray(loc) || loc.length === 0) return null;
  const last = loc[loc.length - 1];
  return typeof last === 'string' ? last : null;
}

/**
 * Extrai erros por campo de respostas 422 (Pydantic / field_errors customizados).
 * Aceita erros no formato Axios (error.response.data).
 */
export function parseApiValidationError(error: unknown): ApiValidationParsed | null {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object' || data === null) return null;

  const d = data as {
    detail?: unknown;
    field_errors?: unknown;
    message?: unknown;
  };

  const fieldErrors: Record<string, string> = {};

  if (d.field_errors && typeof d.field_errors === 'object' && !Array.isArray(d.field_errors)) {
    for (const [k, v] of Object.entries(d.field_errors as Record<string, unknown>)) {
      if (typeof v === 'string' && v.trim()) {
        fieldErrors[k] = friendlyValidationMessage(v);
      }
    }
  }

  if (Array.isArray(d.detail)) {
    for (const item of d.detail) {
      if (!item || typeof item !== 'object') continue;
      const row = item as { loc?: unknown; msg?: unknown };
      const key = lastLocSegment(row.loc);
      const rawMsg = typeof row.msg === 'string' ? row.msg : '';
      if (key && rawMsg && !fieldErrors[key]) {
        fieldErrors[key] = friendlyValidationMessage(rawMsg);
      }
    }
  }

  const keys = Object.keys(fieldErrors);
  if (keys.length === 0) return null;

  let summary = typeof d.message === 'string' ? d.message.trim() : '';
  const pendencia = summary.match(/Há mais \d+ pendência\(s\)[^.]*\.?/i);
  if (pendencia) {
    summary = pendencia[0].trim();
  }
  if (!summary) {
    summary =
      keys.length === 1
        ? 'Há um problema no formulário.'
        : `Há ${keys.length} problemas no formulário.`;
  }

  return { summary, fieldErrors };
}

// Função para formatar erros da API
export const formatApiError = (error: any): string => {
  if (typeof error === 'string') {
    return error;
  }

  const parsed = parseApiValidationError(error);
  if (parsed) {
    const lines = Object.entries(parsed.fieldErrors).map(
      ([field, msg]) => `${field}: ${msg}`
    );
    return [parsed.summary, ...lines].filter(Boolean).join('\n');
  }

  // Se o erro já for um objeto com detail (caso raro)
  if (error?.detail && typeof error.detail === 'string') {
    return error.detail;
  }

  if (error?.response?.data) {
    const data = error.response.data;

    // Se for um erro de validação do Pydantic
    if (data.detail && Array.isArray(data.detail)) {
      return data.detail
        .map((err: any) => {
          const field = lastLocSegment(err.loc) || err.loc?.join('.') || 'campo';
          return `${field}: ${friendlyValidationMessage(String(err.msg || ''))}`;
        })
        .join(', ');
    }
    
    // Se for um erro simples (string)
    if (data.detail && typeof data.detail === 'string') {
      return data.detail;
    }

    // Erro de regra com código: { detail: { code, message } }
    if (typeof data.detail?.message === 'string' && data.detail.message) {
      return data.detail.message;
    }
    
    // Se for um erro de status
    if (data.message) {
      return data.message;
    }
    
    // Se o data inteiro for uma string (caso raro)
    if (typeof data === 'string') {
      return data;
    }
  }
  
  // Se tiver mensagem no erro
  if (error?.message && typeof error.message === 'string') {
    // Não retornar mensagens genéricas do axios
    if (!error.message.includes('Request failed with status code')) {
      return error.message;
    }
  }
  
  // Erro genérico
  return 'Erro desconhecido';
};

/** Mensagem de erro para submit operacional (rede offline ou falha sem response). */
export function resolveNetworkAwareApiError(error: unknown): string {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return 'Sem conexão. Verifique a rede e tente novamente.';
  }

  const err = error as { message?: string; response?: unknown };
  if (err?.message === 'Network Error' || !err?.response) {
    const formatted = formatApiError(error);
    if (formatted !== 'Erro desconhecido') {
      return formatted;
    }
    return 'Falha de comunicação com o servidor. Verifique sua conexão e tente novamente.';
  }

  return formatApiError(error);
}

export const OFFLINE_USER_TITLE = 'Sem conexão';

export const OFFLINE_USER_MESSAGE =
  'Sem conexão. Verifique sua internet e tente novamente.';

export function isLikelyNetworkError(error: unknown): boolean {
  if (!isBrowserOnline()) return true;

  const err = error as { code?: string; message?: string; response?: unknown };
  if (err?.code === 'ERR_NETWORK' || err?.code === 'ECONNABORTED') return true;

  const message = (err?.message ?? '').toLowerCase();
  if (
    message.includes('network error') ||
    message.includes('failed to fetch') ||
    message.includes('internet_disconnected') ||
    message.includes('err_internet_disconnected')
  ) {
    return true;
  }

  return !err?.response;
}

export function networkAwareUserMessage(
  error: unknown,
  fallback: string,
): string {
  return isLikelyNetworkError(error) ? OFFLINE_USER_MESSAGE : fallback;
}

// Função para renderizar erros de forma segura
export const renderError = (error: any): string => {
  if (typeof error === 'string') {
    return error;
  }
  
  return formatApiError(error);
};
